<template>
  <v-card class="side-panel" elevation="1" data-testid="side-panel">
    <v-tabs v-model="activeTab" grow>
      <v-tab data-testid="tab-sensors">{{ $mpt($t('tabs.sensors')) }}</v-tab>
      <v-tab v-if="captureRecordEnabled" data-testid="tab-capture-records">
        {{ $t('tabs.captureRecords') }}
        <v-menu offset-y left content-class="capture-add-menu">
          <template #activator="{ on: menuOn }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltipOn, attrs }">
                <v-btn
                  icon
                  x-small
                  class="capture-tab-add-btn ml-1"
                  v-bind="attrs"
                  v-on="{ ...tooltipOn, ...menuOn }"
                  data-testid="capture-tab-add-button"
                  @click.stop
                >
                  <v-icon x-small>add</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('addTooltip') }}</span>
            </v-tooltip>
          </template>
          <v-list dense>
            <v-tooltip v-if="!hasFloorPlan" bottom>
              <template #activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-list-item disabled data-testid="capture-add-from-map">
                    <v-list-item-icon><v-icon small>place</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('addFromMap') }}</v-list-item-title>
                  </v-list-item>
                </div>
              </template>
              <span>{{ $t('addFromMapDisabledTip') }}</span>
            </v-tooltip>
            <v-list-item v-else @click="$emit('capture-add-from-map')" data-testid="capture-add-from-map">
              <v-list-item-icon><v-icon small>place</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t('addFromMap') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('capture-add-without-position')" data-testid="capture-add-without-position">
              <v-list-item-icon><v-icon small>note_add</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t('addWithoutPosition') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-tab>
      <v-tab data-testid="tab-ranking">{{ $t('tabs.ranking') }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="tabs-content">
      <v-tab-item class="pa-3">
        <floor-plan-item-list
          :items="normalizedItems"
          type="sensor"
          drag-data-key="itemId"
          @remove="$emit('remove', $event)"
        />
      </v-tab-item>
      <v-tab-item v-if="captureRecordEnabled" class="pa-3">
        <capture-record-list
          :capture-records="captureRecords"
          :floor-plan-objects="floorPlanObjects"
          :current-floor-plan-id="currentFloorPlanId"
          :all-floor-plans="allFloorPlans"
          :sensors="sensors"
          data-testid="capture-record-list"
          @edit="$emit('capture-edit', $event)"
          @delete="$emit('capture-delete', $event)"
          @unplace="$emit('capture-unplace', $event)"
        />
      </v-tab-item>
      <v-tab-item class="pa-3">
        <floor-plan-item-ranking
          ref="itemRanking"
          :items="normalizedItems"
          type="sensor"
          :filter-label="detectionFilterLabel"
          :place-name="placeName"
          :floor-plan-name="floorPlanName"
          :selected-month="selectedMonth"
        />
      </v-tab-item>
    </v-tabs-items>
    <ranking-export-actions
      v-if="isRankingTab"
      :exporting="exporting"
      :disabled="!hasRankingData"
      @export-image="handleExportImage"
      @export-csv="handleExportCsv"
    />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  tabs:
    sensors: センサー一覧
    captureRecords: 捕獲記録
    ranking: ランキング
  addTooltip: 捕獲記録を追加する
  addFromMap: 図面から追加
  addFromMapDisabledTip: 図面の登録が必要です
  addWithoutPosition: 配置なしで追加
  export_error: エクスポートに失敗しました
  detection_filter_label:
    exclude_misdetection: 誤検出を除外
    pickup_only: ピックアップのみ
    all: すべて
en:
  tabs:
    sensors: Sensor List
    captureRecords: Capture Records
    ranking: Ranking
  addTooltip: Add capture record
  addFromMap: Add from map
  addFromMapDisabledTip: Floor plan registration required
  addWithoutPosition: Add without position
  export_error: Export failed
  detection_filter_label:
    exclude_misdetection: Exclude false positives
    pickup_only: Pickup only
    all: All
</i18n>

<script>
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import CaptureRecordList from './CaptureRecordList';
import FloorPlanItemList from './FloorPlanItemList';
import FloorPlanItemRanking from './FloorPlanItemRanking';
import RankingExportActions from './RankingExportActions';

export default {
  name: 'SidePanel',
  mixins: [Features, Notifications],
  components: {
    CaptureRecordList,
    FloorPlanItemList,
    FloorPlanItemRanking,
    RankingExportActions,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    captureRecords: {
      type: Array,
      default: () => [],
    },
    currentFloorPlanId: {
      type: String,
      default: null,
    },
    allFloorPlans: {
      type: Array,
      default: () => [],
    },
    sensors: {
      type: Array,
      default: () => [],
    },
    floorPlanObjects: {
      type: Object,
      default: () => ({}),
    },
    placeName: {
      type: String,
      default: '',
    },
    floorPlanName: {
      type: String,
      default: '',
    },
    selectedMonth: {
      type: String,
      default: '',
    },
    detectionFilter: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeTab: 0,
      exporting: false,
    };
  },
  computed: {
    hasFloorPlan() {
      return this.currentFloorPlanId != null;
    },
    captureRecordEnabled() {
      return this.featureIsActive('r_type_capture_record');
    },
    rankingTabIndex() {
      return this.captureRecordEnabled ? 2 : 1;
    },
    normalizedItems() {
      return this.items.map(item => ({
        id: item.itemId,
        label: item.label,
        count: item.count,
        isPlaced: item.isPlaced,
        isPlacedOnOtherFloorPlan: item.isPlacedOnOtherFloorPlan,
        placedFloorPlanName: item.placedFloorPlanName,
        type: item.type,
        status: item.status,
      }));
    },
    isRankingTab() {
      return this.activeTab === this.rankingTabIndex;
    },
    hasRankingData() {
      return this.normalizedItems.some(item => item.isPlaced);
    },
    detectionFilterLabel() {
      if (!this.detectionFilter) return '';
      return this.$t(`detection_filter_label.${this.detectionFilter}`);
    },
  },
  methods: {
    async handleExportImage() {
      this.exporting = true;
      try {
        await this.$refs.itemRanking.exportImage();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.notifyError('export_error');
      } finally {
        this.exporting = false;
      }
    },
    async handleExportCsv() {
      this.exporting = true;
      try {
        this.$refs.itemRanking.exportCsv();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.notifyError('export_error');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.side-panel {
  --toolbar-height: 162px;
  position: sticky;
  top: var(--toolbar-height);
  z-index: 10;
  max-height: calc(100vh - var(--toolbar-height));
  display: flex;
  flex-direction: column;
}

.tabs-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.capture-tab-add-btn {
  margin-top: -2px;
  background-color: #e0e0e0 !important; /* v-btn icon のインラインtransparentを上書き */
}

.capture-tab-add-btn:hover {
  background-color: #bdbdbd !important; /* v-btn icon のインラインtransparentを上書き */
}
</style>

<style>
/* v-menuのコンテンツはbody直下にテレポートされるためscopedが効かない。
   Vuetifyの `.v-application--is-ltr .v-list-item__icon:first-child` (0,3,0) に勝つため !important。 */
.capture-add-menu .v-list-item__icon,
.capture-add-menu .v-list-item__icon:first-child {
  min-width: unset !important;
  margin: 0 4px 0 0 !important;
  align-self: center;
}
</style>
